import { clsx } from 'clsx';
import { memo } from 'react';
import type { ProductCard as ProductCardData } from '@sbg/shared/schemas';
import { useT } from '@/lib/i18n';
import { Reveal } from '@/motion/Reveal';
import { ProductCard } from './ProductCard';
import { ProductCardSkeleton } from './ProductCardSkeleton';

export interface ProductGridProps {
  products: readonly ProductCardData[];
  /** Show skeleton cards instead of (or after) the products. */
  loading?: boolean;
  skeletonCount?: number;
  /** How many of the first cards load their photo eagerly. Default: one row (4). */
  priorityCount?: number;
  /** `grid` (default) or `list` — one wide row per product where the container is wide enough. */
  view?: 'grid' | 'list';
  /** `sizes` override for layouts that are narrower than the page (filter sidebar, carousels). */
  sizes?: string;
  className?: string;
}

/**
 * Responsive grid of product cards. Columns follow the width of the *container*, not the window, so
 * the same grid works full-width and beside a filter sidebar: 1 column, 2 from 560 px, 3 from
 * 900 px, 4 from 1200 px. Each card rises in as it scrolls into view, staggered across a row.
 */
export const ProductGrid = memo(function ProductGrid({
  products,
  loading = false,
  skeletonCount = 8,
  priorityCount = 4,
  view = 'grid',
  sizes,
  className,
}: ProductGridProps) {
  const t = useT();
  return (
    <div className={clsx('product-grid-host', className)}>
      <ul className="product-grid" data-view={view} aria-busy={loading || undefined}>
        {products.map((product, index) => (
          <Reveal
            as="li"
            variant="rise"
            key={product.id}
            delay={(index % 4) * 80}
            className="product-grid__item"
          >
            <ProductCard
              product={product}
              priority={index < priorityCount}
              {...(sizes ? { sizes } : {})}
            />
          </Reveal>
        ))}
        {loading
          ? Array.from({ length: skeletonCount }, (_, index) => (
              <li key={`skeleton-${String(index)}`} className="product-grid__item">
                <ProductCardSkeleton />
              </li>
            ))
          : null}
      </ul>
      {loading ? (
        <p className="sr-only" role="status">
          {t('common.loading')}
        </p>
      ) : null}
    </div>
  );
});
